import { logger } from '../utils/logger';
import { LoadStats } from '../loader/load-stats';
const AGE_HEADER_LINE_REGEX = /^age:\s*[\d.]+\s*$/m;
class XhrLoader {
    constructor(config) {
        this.config = null;
        this.callbacks = null;
        this.loader = null;
        this.xhrSetup = config ? config.xhrSetup : null;
        this.stats = new LoadStats();
        this.retryDelay = 0;
    }
    destroy() {
        this.callbacks = null;
        this.abortInternal();
        this.loader = null;
        this.config = null;
    }
    abortInternal() {
        const loader = this.loader;
        self.clearTimeout(this.requestTimeout);
        self.clearTimeout(this.retryTimeout);
        if (loader) {
            loader.onreadystatechange = null;
            loader.onprogress = null;
            if (loader.readyState !== 4) {
                this.stats.aborted = true;
                loader.abort();
            }
        }
    }
    abort() {
        var _a;
        this.abortInternal();
        if ((_a = this.callbacks) === null || _a === void 0 ? void 0 : _a.onAbort) {
            this.callbacks.onAbort(this.stats, this.context, this.loader);
        }
    }
    load(context, config, callbacks) {
        if (this.stats.loading.start) {
            throw new Error('Loader can only be used once.');
        }
        this.stats.loading.start = self.performance.now();
        this.context = context;
        this.config = config;
        this.callbacks = callbacks;
        this.retryDelay = config.retryDelay;
        this.loadInternal();
    }
    loadInternal() {
        const { config, context } = this;
        if (!config) {
            return;
        }
        const xhr = (this.loader = new self.XMLHttpRequest());
        const stats = this.stats;
        stats.loading.first = 0;
        stats.loaded = 0;
        const xhrSetup = this.xhrSetup;
        try {
            if (xhrSetup) {
                try {
                    xhrSetup(xhr, context.url);
                }
                catch (e) {
                    xhr.open('GET', context.url, true);
                    xhrSetup(xhr, context.url);
                }
            }
            if (!xhr.readyState) {
                xhr.open('GET', context.url, true);
            }
            const headers = this.context.headers;
            if (headers) {
                for (const header in headers) {
                    xhr.setRequestHeader(header, headers[header]);
                }
            }
        }
        catch (e) {
            this.callbacks.onError({ code: xhr.status, text: e.message }, context, xhr);
            return;
        }
        if (context.rangeEnd) {
            xhr.setRequestHeader('Range', 'bytes=' + context.rangeStart + '-' + (context.rangeEnd - 1));
        }
        xhr.onreadystatechange = this.readystatechange.bind(this);
        xhr.onprogress = this.loadprogress.bind(this);
        xhr.responseType = context.responseType;
        self.clearTimeout(this.requestTimeout);
        this.requestTimeout = self.setTimeout(this.loadtimeout.bind(this), config.timeout);
        xhr.send();
    }
    readystatechange() {
        const { context, loader: xhr, stats } = this;
        if (!context || !xhr) {
            return;
        }
        const readyState = xhr.readyState;
        const config = this.config;
        if (stats.aborted) {
            return;
        }
        if (readyState >= 2) {
            self.clearTimeout(this.requestTimeout);
            if (stats.loading.first === 0) {
                stats.loading.first = Math.max(self.performance.now(), stats.loading.start);
            }
            if (readyState === 4) {
                xhr.onreadystatechange = null;
                xhr.onprogress = null;
                const status = xhr.status;
                const isArrayBuffer = xhr.responseType === 'arraybuffer';
                if (status >= 200 &&
                    status < 300 &&
                    ((isArrayBuffer && xhr.response) || xhr.responseText !== null)) {
                    stats.loading.end = Math.max(self.performance.now(), stats.loading.first);
                    let data;
                    let len;
                    if (isArrayBuffer) {
                        data = xhr.response;
                        len = data.byteLength;
                    }
                    else {
                        data = xhr.responseText;
                        len = data.length;
                    }
                    stats.loaded = stats.total = len;
                    if (!this.callbacks) {
                        return;
                    }
                    const onProgress = this.callbacks.onProgress;
                    if (onProgress) {
                        onProgress(stats, context, data, xhr);
                    }
                    if (!this.callbacks) {
                        return;
                    }
                    const response = {
                        url: xhr.responseURL,
                        data: data,
                    };
                    this.callbacks.onSuccess(response, stats, context, xhr);
                }
                else {
                    if (stats.retry >= config.maxRetry ||
                        (status >= 400 && status < 499)) {
                        logger.error(`${status} while loading ${context.url}`);
                        this.callbacks.onError({ code: status, text: xhr.statusText }, context, xhr);
                    }
                    else {
                        logger.warn(`${status} while loading ${context.url}, retrying in ${this.retryDelay}...`);
                        this.abortInternal();
                        this.loader = null;
                        self.clearTimeout(this.retryTimeout);
                        this.retryTimeout = self.setTimeout(this.loadInternal.bind(this), this.retryDelay);
                        this.retryDelay = Math.min(2 * this.retryDelay, config.maxRetryDelay);
                        stats.retry++;
                    }
                }
            }
            else {
                self.clearTimeout(this.requestTimeout);
                this.requestTimeout = self.setTimeout(this.loadtimeout.bind(this), config.timeout);
            }
        }
    }
    loadtimeout() {
        logger.warn(`timeout while loading ${this.context.url}`);
        const callbacks = this.callbacks;
        if (callbacks) {
            this.abortInternal();
            callbacks.onTimeout(this.stats, this.context, this.loader);
        }
    }
    loadprogress(event) {
        const stats = this.stats;
        stats.loaded = event.loaded;
        if (event.lengthComputable) {
            stats.total = event.total;
        }
    }
    getCacheAge() {
        let result = null;
        if (this.loader &&
            AGE_HEADER_LINE_REGEX.test(this.loader.getAllResponseHeaders())) {
            const ageHeader = this.loader.getResponseHeader('age');
            result = ageHeader ? parseFloat(ageHeader) : null;
        }
        return result;
    }
}
export default XhrLoader;
